// Excel-style column filter: a search box over a checkbox list of the values that are
// actually in the column, instead of a free-text box you have to type blind into.
//
// The complaint it answers, from the accounts side: "cannot, must write ourself, don't have
// this dropdown can check back like this ya" — you could only filter a column by typing a
// value you already knew was there, with no way to see what the column contains or to pick
// several at once. (user 2026-10-06)
//
// Drop it onto a column with `facetFilter` from this file:
//   { id: 'status', header: 'Status', accessorKey: 'status', ...facetFilter }
// and switch `enableFacetedValues: true` on the table, which is what fills the list.
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Checkbox, Divider, FormControlLabel, InputAdornment, TextField, Typography, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { MRT_FilterFns } from 'material-react-table';

const PP = "'Poppins', sans-serif";
const BLANK = '— blank —';   // a real row with no value still needs a tick box

// Numbers sort as numbers, everything else alphabetically. "110.25%" after "63.79%", not
// before it, which is the whole reason the Excel list was readable in the first place.
const compareValues = (a, b) => {
    const na = parseFloat(String(a).replace(/[^0-9.-]/g, ''));
    const nb = parseFloat(String(b).replace(/[^0-9.-]/g, ''));
    const aNum = !isNaN(na) && /\d/.test(String(a));
    const bNum = !isNaN(nb) && /\d/.test(String(b));
    if (aNum && bNum && na !== nb) return na - nb;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// A column where nearly every row holds a different value — a sale id, a price, a date —
// has nothing to offer as a list: it would print one line per row. Those keep the ordinary
// "contains" box. Everything else gets the tick list, so a table can hand this filter to
// EVERY column and each one settles into the right shape on its own.
const MANY = 50;
const isNearlyUnique = (distinct, rows) => distinct > MANY && distinct > rows * 0.9;

// Material-React-Table pins its filter popover ABOVE the column — `anchorOrigin` vertical
// 'top' with `transformOrigin` vertical 'bottom', both hardcoded, with no prop to change
// them. On a tall list that lands over the toolbar instead of under the header you clicked,
// which is not where anyone looks for it. (user 2026-10-06: "when we click description then
// show under the description")
//
// So the paper is moved once, as it opens, to sit just under that column's header cell. The
// header is found by the aria-label of the button that opened it, which MRT builds from the
// same header text we have here. Anything unexpected — no paper, no header, no room below —
// and it is left exactly where the library put it.
const useDropBelowHeader = (headerText) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        // Two frames, because MUI writes the paper's `top` itself AFTER mount — moving it
        // in the same tick just gets overwritten.
        const place = () => {
            try {
                const paper = ref.current?.closest('.MuiPopover-paper');
                if (!paper) return;
                const btn = document.querySelector(`thead button[aria-label="Filter by ${headerText}"]`);
                const cell = btn?.closest('th');
                if (!cell) return;

                const below = cell.getBoundingClientRect().bottom + 4;
                const height = paper.getBoundingClientRect().height;
                if (below + height > window.innerHeight - 8) return;   // no room; leave it alone

                paper.style.top = `${below}px`;
                paper.style.transformOrigin = 'center top';
            } catch { /* positioning is a nicety — never let it break the filter */ }
        };
        const id = requestAnimationFrame(() => requestAnimationFrame(place));
        return () => cancelAnimationFrame(id);
    }, [headerText]);
    return ref;
};

export default function FacetFilter({ column }) {
    const [search, setSearch] = useState('');
    const boxRef = useDropBelowHeader(String(column?.columnDef?.header ?? ''));

    // Every distinct value in this column, with how many rows carry it.
    //
    // Keyed on the FACET MAP, not on `column`. The table renders its filters once, up front —
    // before the rows have arrived from the API — and the column object keeps the same
    // identity for the life of the table. Memoised on `column` this list latched onto the
    // empty first render and never recovered, so every filter on every page came up as the
    // plain box. react-table hands back a new map when the data changes, which is the signal
    // that matters. (found on prod, 2026-10-06)
    const facets = column.getFacetedUniqueValues?.() ?? new Map();
    const options = useMemo(() => {
        const counts = new Map();
        facets.forEach((n, raw) => {
            const key = raw === null || raw === undefined || String(raw).trim() === '' ? BLANK : String(raw);
            counts.set(key, (counts.get(key) || 0) + n);
        });
        return [...counts.entries()]
            .sort((a, b) => compareValues(a[0], b[0]))
            .map(([value, count]) => ({ value, count }));
    }, [facets]);

    const rowCount = useMemo(
        () => options.reduce((n, o) => n + o.count, 0), [options]);

    // No filter set = everything is in. That matches Excel, where a fresh column opens with
    // every box ticked rather than none.
    const selected = column.getFilterValue();

    const isOn = (v) => (Array.isArray(selected) ? selected.includes(v) : true);

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? options.filter(o => o.value.toLowerCase().includes(q)) : options;
    }, [options, search]);

    // Nothing worth listing — behave like the plain filter this replaced.
    if (options.length === 0 || isNearlyUnique(options.length, rowCount)) {
        return (
            <TextField
                ref={boxRef}
                size="small" fullWidth autoFocus placeholder="Filter"
                value={typeof selected === 'string' ? selected : ''}
                onChange={(e) => column.setFilterValue(e.target.value || undefined)}
                sx={{ width: 200 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
                        </InputAdornment>
                    ),
                    sx: { fontFamily: PP, fontSize: '0.8rem', borderRadius: '8px' },
                }}
            />
        );
    }

    const allShownOn = shown.length > 0 && shown.every(o => isOn(o.value));

    const commit = (next) => {
        // Everything ticked is the same as no filter — store it that way so the column header
        // does not keep showing a filter badge for a filter that excludes nothing.
        column.setFilterValue(next.length === options.length ? undefined : next);
    };

    const toggle = (value) => {
        const base = Array.isArray(selected) ? selected : options.map(o => o.value);
        commit(base.includes(value) ? base.filter(v => v !== value) : [...base, value]);
    };

    // Select All acts on WHAT IS SHOWN, so searching then ticking the box is a quick way to
    // take a whole group; values filtered out of view keep whatever state they had.
    const toggleAllShown = () => {
        const base = Array.isArray(selected) ? selected : options.map(o => o.value);
        const shownValues = shown.map(o => o.value);
        commit(allShownOn
            ? base.filter(v => !shownValues.includes(v))
            : [...new Set([...base, ...shownValues])]);
    };

    return (
        <Box ref={boxRef} sx={{ width: 248, pt: 0.5 }} onKeyDown={(e) => e.stopPropagation()}>
            <TextField
                size="small" fullWidth autoFocus placeholder="Search"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
                        </InputAdornment>
                    ),
                    sx: { fontFamily: PP, fontSize: '0.8rem', borderRadius: '8px' },
                }}
            />

            <FormControlLabel
                sx={{ mt: 0.75, ml: 0 }}
                control={<Checkbox size="small" checked={allShownOn} indeterminate={!allShownOn && shown.some(o => isOn(o.value))} onChange={toggleAllShown} />}
                label={<Typography sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 700 }}>
                    {search.trim() ? `Select all ${shown.length} shown` : 'Select all'}
                </Typography>}
            />
            <Divider />

            <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5 }}>
                {shown.length === 0 && (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#94A3B8', py: 1.5, textAlign: 'center' }}>
                        Nothing matches "{search}"
                    </Typography>
                )}
                {shown.map(o => (
                    <FormControlLabel
                        key={o.value}
                        sx={{ display: 'flex', ml: 0, mr: 0, '& .MuiFormControlLabel-label': { minWidth: 0, flex: 1 } }}
                        control={<Checkbox size="small" checked={isOn(o.value)} onChange={() => toggle(o.value)} />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontFamily: PP, fontSize: '0.78rem', flex: 1, minWidth: 0 }}>
                                    {o.value}
                                </Typography>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>{o.count}</Typography>
                            </Box>
                        }
                    />
                ))}
            </Box>

            <Divider sx={{ mt: 0.5 }} />
            <Button
                size="small" fullWidth disabled={!Array.isArray(selected)}
                onClick={() => { setSearch(''); column.setFilterValue(undefined); }}
                sx={{ fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', mt: 0.5 }}
            >
                Clear filter
            </Button>
        </Box>
    );
}

// Serves BOTH shapes, because a column does not know in advance which it will show: an
// array from the tick list, a string from the text box a near-unique column falls back to.
// Written out rather than left to `arrIncludesSome`, which has no notion of an empty cell.
export const facetFilterFn = (row, columnId, filterValue) => {
    const raw = row.getValue(columnId);
    if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) return false;    // every box cleared = show nothing
        const key = raw === null || raw === undefined || String(raw).trim() === '' ? BLANK : String(raw);
        return filterValue.includes(key);
    }
    const q = String(filterValue ?? '').trim().toLowerCase();
    return q === '' || String(raw ?? '').toLowerCase().includes(q);
};

// ── Wiring ──────────────────────────────────────────────────────────────────
// Material-React-Table does NOT read `filterFn` off `defaultColumn`. It picks each column's
// filter function from that column's own `filterVariant` (see getDefaultColumnFilterFn in
// the library), falling back to `fuzzy` — and fuzzy, handed an ARRAY of ticked values,
// matches nothing. That is how the first attempt reached prod looking right and emptying the
// table on the first tick.
//
// So the variant goes on each column, which routes it to `arrIncludesSome`, and that name is
// pointed at our own function through the table's `filterFns`. Both halves are required;
// neither works alone.
//
//   const table = useMaterialReactTable({
//       ...facetFilterOptions,
//       columns: withFacetFilter(columns),
//       data,
//   });
export const facetFilterOptions = {
    enableFacetedValues: true,          // fills the tick lists
    columnFilterDisplayMode: 'popover',
    // Every column's filter function defaults to `fuzzy`, and `defaultColumn.filterFn` is
    // not consulted — so fuzzy itself is where this has to go in. A STRING is handed
    // straight back to the real fuzzy, leaving global search and the fallback box exactly
    // as they were; an ARRAY is the tick list, which fuzzy has no notion of and silently
    // matched nothing against.
    //
    // The route MRT documents — `filterVariant: 'multi-select'` — is not usable here: it
    // makes the library build and SORT its own option list, which throws
    // "a.localeCompare is not a function" on any column holding numbers.
    filterFns: {
        fuzzy: (row, columnId, value, meta) => (
            Array.isArray(value)
                ? facetFilterFn(row, columnId, value)
                : MRT_FilterFns.fuzzy(row, columnId, value, meta)
        ),
    },
    defaultColumn: { Filter: ({ column }) => <FacetFilter column={column} /> },
};
